import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useBrand } from "../lib/brand";
import { api, API } from "../lib/api";
import {
  LayoutDashboard, Users, Layers, Shirt, Package, Truck,
  ClipboardList, CalendarClock, Warehouse, Sparkles, LogOut, Scissors,
  Settings, Shield, ChevronsUpDown, Plus, ArrowLeftRight, Activity, UserPlus,
  FolderOpen, MessageSquare, Bell, Calculator, Inbox, Factory,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import NotificationBell from "./NotificationBell";
import { useEventStream } from "../lib/events";
import { useDmNotifier } from "../lib/dmNotifier";
import { useI18n } from "../lib/i18n";

const NAV_KEYS = [
  { to: "/dashboard", labelKey: "nav.dashboard", fallback: "Dashboard", icon: LayoutDashboard },
  { to: "/clients-orders", labelKey: "clients_orders.eyebrow", fallback: "Clienti & Ordini", icon: Users },
  { to: "/collections-garments", labelKey: "collections_garments.eyebrow", fallback: "Collezioni & Capi", icon: Layers },
  { to: "/materials-inventory", labelKey: "materials_inventory.eyebrow", fallback: "Materiali & Magazzino", icon: Package },
  { to: "/productions", labelKey: "nav.productions", fallback: "Produzione", icon: Factory },
  { to: "/suppliers", labelKey: "nav.suppliers", fallback: "Suppliers", icon: Truck },
  { to: "/tasks", labelKey: "nav.tasks", fallback: "Tasks", icon: CalendarClock },
  { to: "/folder", labelKey: "nav.folder", fallback: "Brand Folder", icon: FolderOpen },
  { to: "/chat", labelKey: "nav.chat", fallback: "Team Chat", icon: MessageSquare },
  { to: "/ai", labelKey: "nav.ai", fallback: "AI Assistant", icon: Sparkles },
  { to: "/mms-simulator", labelKey: "nav.mms", fallback: "MMS Price Simulator", icon: Calculator },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  useEventStream();
  useDmNotifier();
  const { brands, activeBrand, select, refresh, themeMode } = useBrand();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef();

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const switchBrand = async (bid) => {
    setOpen(false);
    await select(bid);
    window.location.reload();
  };

  const exitWorkspace = async () => {
    setOpen(false);
    try { await api.post("/brands/exit"); } catch { /* noop */ }
    await refresh();
    nav("/select-brand");
  };

  // Read the effective accent color from CSS custom property so the sanitiser
  // in applyTheme() takes effect (drops legacy gold from stale DB rows).
  const [accent, setAccent] = useState("#0A0A0A");
  useEffect(() => {
    const v = getComputedStyle(document.documentElement)
      .getPropertyValue("--brand-accent").trim();
    if (v) setAccent(v);
  }, [activeBrand?.id, activeBrand?.theme]);

  return (
    <div className="min-h-screen flex bg-[var(--bg)] text-[#0A0A0A]">
      <aside className="hidden md:flex md:flex-col w-64 border-r border-[#E5E5E5] bg-[var(--sidebar,#FAFAFA)]">
        <div className="px-6 py-6 border-b" style={{ borderColor: "var(--sidebar-border)" }}>
          <div className="flex items-center gap-3 mb-4">
            <img
              src={themeMode === "dark" ? "/branding/logo-mms-white.svg" : "/branding/logo-mms-black.svg"}
              alt="MMS"
              style={{ height: 28, width: "auto" }}
            />
            <div>
              <div className="font-serif text-xl tracking-wider" style={{ color: "var(--text-primary)" }}>MMS HUB CENTER</div>
              <div className="label-caps mt-1">Fashion ERP</div>
            </div>
          </div>

          {/* current brand + switcher */}
          <div className="mb-3">
            <div className="text-[9px] uppercase tracking-widest text-[#737373] mb-1">Currently working in</div>
            <div className="relative" ref={menuRef}>
              <button
                data-testid="brand-switcher"
                onClick={()=>setOpen(!open)}
                className="w-full flex items-center justify-between border p-2 hover:border-[#0A0A0A] transition-colors"
                style={{ borderColor: open ? accent : "#E5E5E5" }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {activeBrand?.logo_file_id ? (
                    <div
                      className="w-7 h-7 flex items-center justify-center overflow-hidden"
                      style={{ background: "var(--surface-tertiary)", border: "1px solid var(--border)" }}
                    >
                      <img
                        src={`${API}/files/${activeBrand.logo_file_id}?variant=thumb`}
                        alt={activeBrand?.name || "brand"}
                        className="max-w-full max-h-full object-contain"
                        data-testid="sidebar-brand-logo"
                      />
                    </div>
                  ) : (
                    <div className="w-7 h-7 flex items-center justify-center border text-[11px]" style={{ background: accent + "1A", color: accent, borderColor: accent + "80" }}>
                      {activeBrand?.name?.[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                  <div className="text-xs truncate font-medium" data-testid="active-brand-name">{activeBrand?.name || "No brand"}</div>
                </div>
                <ChevronsUpDown size={12} className="text-[#525252]"/>
              </button>
              {open && (
                <div className="absolute top-full left-0 right-0 mt-1 card-dark z-30 max-h-72 overflow-auto" data-testid="brand-menu">
                  <div className="p-2 text-[9px] uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5]">Switch to</div>
                  {brands.filter(b => b.id !== activeBrand?.id).map(b => (
                    <button
                      key={b.id}
                      data-testid={`switch-brand-${b.id}`}
                      onClick={()=>switchBrand(b.id)}
                      className="w-full flex items-center justify-between p-2 text-xs hover:bg-black/5 text-left"
                    >
                      <span className="truncate">{b.name}</span>
                    </button>
                  ))}
                  {brands.length <= 1 && (
                    <div className="p-2 text-xs text-[#737373] italic">No other brands</div>
                  )}
                  <button onClick={exitWorkspace} data-testid="exit-workspace-btn" className="w-full flex items-center gap-2 p-2 text-xs text-[#0A0A0A] hover:bg-black/5 border-t border-[#E5E5E5]">
                    <ArrowLeftRight size={12}/> Exit to Brand Accounts
                  </button>
                  <button onClick={()=>{setOpen(false); nav("/select-brand");}} className="w-full flex items-center gap-2 p-2 text-xs text-[#525252] hover:bg-black/5 border-t border-[#E5E5E5]">
                    <Plus size={12}/> Add new brand
                  </button>
                </div>
              )}
            </div>
            <button
              data-testid="switch-brand-btn"
              onClick={exitWorkspace}
              className="mt-2 w-full text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A] transition-colors inline-flex items-center gap-1 justify-center border border-[#E5E5E5] py-1.5"
            >
              <ArrowLeftRight size={10}/> Switch Brand
            </button>
          </div>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {NAV_KEYS.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                data-testid={`nav-${n.to.slice(1)}`}
              >
                <Icon size={16} strokeWidth={1.5}/>
                <span>{t(n.labelKey, n.fallback)}</span>
              </NavLink>
            );
          })}
          <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
            <NavLink to="/team" className={({isActive}) => `nav-item ${isActive?"active":""}`} data-testid="nav-team">
              <UserPlus size={16} strokeWidth={1.5}/><span>Team Members</span>
            </NavLink>
            <NavLink to="/settings/brand" className={({isActive}) => `nav-item ${isActive?"active":""}`} data-testid="nav-brand-settings">
              <Settings size={16} strokeWidth={1.5}/><span>Brand Settings</span>
            </NavLink>
            <NavLink to="/settings/notifications" className={({isActive}) => `nav-item ${isActive?"active":""}`} data-testid="nav-notif-prefs">
              <Bell size={16} strokeWidth={1.5}/><span>Notifications</span>
            </NavLink>
            <NavLink to="/audit-logs" className={({isActive}) => `nav-item ${isActive?"active":""}`} data-testid="nav-audit-logs">
              <Activity size={16} strokeWidth={1.5}/><span>Audit Log</span>
            </NavLink>
            {user?.is_super_admin && (
              <>
                <NavLink to="/admin" className={({isActive}) => `nav-item ${isActive?"active":""}`} data-testid="nav-super-admin">
                  <Shield size={16} strokeWidth={1.5}/><span>Super Admin</span>
                </NavLink>
                <NavLink to="/admin/mms-pricing" className={({isActive}) => `nav-item ${isActive?"active":""}`} data-testid="nav-mms-pricing">
                  <Calculator size={16} strokeWidth={1.5}/><span>MMS Pricing</span>
                </NavLink>
                <NavLink to="/admin/mms-simulations" className={({isActive}) => `nav-item ${isActive?"active":""}`} data-testid="nav-mms-simulations">
                  <Inbox size={16} strokeWidth={1.5}/><span>MMS Quote Requests</span>
                </NavLink>
                <NavLink to="/settings/email-provider" className={({isActive}) => `nav-item ${isActive?"active":""}`} data-testid="nav-email-provider">
                  <Bell size={16} strokeWidth={1.5}/><span>Email Provider</span>
                </NavLink>
              </>
            )}

            {/* Prominent Exit Workspace */}
            <button
              onClick={exitWorkspace}
              data-testid="sidebar-exit-workspace"
              className="mx-4 mt-4 w-[calc(100%-2rem)] text-[10px] uppercase tracking-widest text-white bg-[#0A0A0A] hover:bg-[#171717] transition-colors inline-flex items-center gap-2 justify-center py-2 px-3"
            >
              <ArrowLeftRight size={12}/> Back to Brand Accounts
            </button>
          </div>
        </nav>
        <div className="border-t border-[#E5E5E5] p-4">
          <div className="flex items-center gap-1 mb-3" data-testid="lang-switcher">
            {["it","en"].map(l => (
              <button key={l}
                data-testid={`lang-${l}`}
                onClick={()=>setLang(l)}
                className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${lang===l ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "text-[#525252] border-[#E5E5E5] hover:text-[#0A0A0A]"}`}
              >{l}</button>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <div className="text-xs min-w-0">
              <div className="text-[#0A0A0A] truncate">{user?.name}</div>
              <div className="text-[#737373] uppercase tracking-widest text-[10px]">{user?.is_super_admin ? "super admin" : user?.role?.replaceAll("_"," ")}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); nav("/login"); }}
              className="text-[#525252] hover:text-[#0A0A0A] transition-colors"
              title="Logout"
            >
              <LogOut size={16} strokeWidth={1.5}/>
            </button>
          </div>
        </div>
      </aside>

      {/* mobile top-bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 glass z-40 flex items-center justify-between px-4 py-3">
        <div className="flex flex-col min-w-0">
          <div className="text-[9px] uppercase tracking-widest text-[#737373]">Currently in</div>
          <div className="font-serif tracking-wide truncate" style={{ color: accent }}>{activeBrand?.name || "MMS HUB CENTER"}</div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell variantId="mobile"/>
          <button onClick={exitWorkspace} data-testid="mobile-switch-brand" className="btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1">
            <ArrowLeftRight size={10}/> Switch
          </button>
          <select
            data-testid="mobile-nav-select"
            className="input-dark w-36 text-xs py-1"
            onChange={(e) => nav(e.target.value)}
            value={window.location.pathname}
          >
            {NAV_KEYS.map((n) => (
              <option key={n.to} value={n.to}>{t(n.labelKey, n.fallback)}</option>
            ))}
            <option value="/settings/brand">Brand Settings</option>
            {user?.is_super_admin && <option value="/admin">Super Admin</option>}
          </select>
        </div>
      </div>

      <main className="flex-1 md:ml-0 mt-14 md:mt-0 min-w-0">
        {/* Desktop topbar with notification bell */}
        <div className="hidden md:flex items-center justify-end px-6 md:px-10 pt-6">
          <NotificationBell/>
        </div>
        <div className="max-w-[1400px] p-6 md:p-10 md:pt-4">
          {children}
        </div>
      </main>
    </div>
  );
}
